// =============================================================
// 📁 src/pages/planificateur/PointsParDescente.tsx
// 💎 Platinum PRO — Points QR par descente (ALIGNÉ BACKEND)
// =============================================================

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { api } from "../../../lib/axios";
import { useAuth } from "../../../context/AuthContext";

// =============================================================
// 🧩 Types alignés backend
// =============================================================
type SecteurPoint =
  | "MENAGE"
  | "PME"
  | "INDUSTRIE"
  | "ONG"
  | "INSTITUTION"
  | "HOPITAL"
  | "ECOLE"
  | "UNIVERSITE"
  | "EGLISE"
  | "MINISTERE"
  | "MARCHE";

interface PointForm {
  ordre: number;
  secteur: SecteurPoint;
  qr: string;
  clientId?: number | null;   // ✅ IMPORTANT
  nom?: string | null;
  adresse?: string | null;
  commentaire?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

const LOCAL_KEY = "points-descente-";

// Petit helper UI label (optionnel)
const secteurLabel: Record<SecteurPoint, string> = {
  MENAGE: "Ménage",
  PME: "PME",
  INDUSTRIE: "Industrie",
  ONG: "ONG",
  INSTITUTION: "Institution",
  HOPITAL: "Hôpital",
  ECOLE: "École",
  UNIVERSITE: "Université",
  EGLISE: "Église",
  MINISTERE: "Ministère",
  MARCHE: "Marché",
};

const defaultPoint = (ordre: number): PointForm => ({
  ordre,
  secteur: "MENAGE",
  qr: "",
  nom: null,
  adresse: null,  // ✅ AJOUT
  commentaire: null,
  latitude: null,
  longitude: null,
});

// =============================================================
// 🌟 Composant principal
// =============================================================
const PointsParDescente: React.FC = () => {

const navigate = useNavigate();
const [isLocked, setIsLocked] = useState(false);
const { user, token } = useAuth() as any;
const canEdit = user?.role?.toUpperCase() === "PLANIFICATEUR";
const { id } = useParams<{ id: string }>();
const descenteId = id && !isNaN(Number(id)) ? Number(id) : null;
const [descenteNumero, setDescenteNumero] = useState<string | null>(null);

useEffect(() => {
  if (!descenteId || !token) return;

  const loadDescente = async () => {
    try {
      const res = await api.get(`/descentes/${descenteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data;
      if (d) {
        setDescenteNumero(d.numero); // ✅ récupérer le numéro unique
      }
    } catch (err) {
      console.error("Erreur chargement descente :", err);
    }
  };

  loadDescente();
}, [descenteId, token]);

useEffect(() => {
  if (!descenteId || isNaN(descenteId)) {
    navigate("/admin/planificateur");
  }
}, [descenteId, navigate]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [isOnline, setIsOnline] = useState(
  typeof navigator !== "undefined" ? navigator.onLine : true
);

  const role = localStorage.getItem("role");


  const [points, setPoints] = useState<PointForm[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState<PointForm>(defaultPoint(1));

// =============================================================
// 🔄 Charger points depuis backend (fallback localStorage sécurisé)
// =============================================================
useEffect(() => {
  if (!descenteId || !token) return;

  let cancelled = false;
 
  const loadFromLocalStorage = (): boolean => {
    try {
      const saved = localStorage.getItem(`${LOCAL_KEY}${descenteId}`);
      if (!saved) return false;

      const parsed = JSON.parse(saved);

      if (Array.isArray(parsed) && parsed.length > 0) {
        if (!cancelled) {
          setPoints(parsed);
          setForm(defaultPoint(parsed.length + 1));
        }
        return true;
      }
    } catch (e) {
      console.error("Erreur parsing localStorage:", e);
    }

    return false;
  };

  const load = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/descentes/${descenteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (cancelled) return;

      const d = res.data?.data;

      // 🔒 Verrouillage métier
      const statut = d?.statut;
      setIsLocked(statut === "CLOTUREE" || statut === "ANNULEE");

      const backendPoints: PointForm[] = Array.isArray(d?.points)
        ? d.points
            .slice()
            .sort((a: any, b: any) => (a?.ordre ?? 0) - (b?.ordre ?? 0))
            .map((p: any, idx: number) => ({
              ordre:
                Number.isInteger(p?.ordre) && p.ordre > 0
                  ? p.ordre
                  : idx + 1,

              secteur:
                typeof p?.secteur === "string"
                  ? (p.secteur as SecteurPoint)
                  : "MENAGE",

              qr: String(p?.qr ?? "-").trim(),

              nom: p?.nom ?? null,
              adresse: p?.adresse ?? null,
              commentaire: p?.commentaire ?? null,

              latitude:
                typeof p?.latitude === "number"
                  ? p.latitude
                  : null,

              longitude:
                typeof p?.longitude === "number"
                  ? p.longitude
                  : null,
            }))
        : [];

      if (backendPoints.length > 0) {
        setPoints(backendPoints);
        setForm(defaultPoint(backendPoints.length + 1));
      } else {
        loadFromLocalStorage();
      }

    } catch (err) {
      console.error("Erreur chargement points:", err);

      const loaded = loadFromLocalStorage();

      if (!loaded) {
        toast.error("❌ Impossible de charger la descente/points.");
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  load();

  return () => {
    cancelled = true;
  };
}, [descenteId, token]);

// =============================================================
// 💾 Sauvegarde locale auto (sécurité offline)
// =============================================================
useEffect(() => {
  if (!descenteId) return;

  if (points.length > 0) {
    localStorage.setItem(
      `${LOCAL_KEY}${descenteId}`,
      JSON.stringify(points)
    );
  }
}, [points, descenteId]);
 
useEffect(() => {
  const update = () => setIsOnline(navigator.onLine);

  window.addEventListener("online", update);
  window.addEventListener("offline", update);

  return () => {
    window.removeEventListener("online", update);
    window.removeEventListener("offline", update);
  };
}, []);

useEffect(() => {
  if (isOnline && isDirty && points.length > 0) {
    envoyerVersBackend();
  }
}, [isOnline]); // ok volontairement

// =============================================================
// ➕ Ajout d’un point (QR unique sécurisé)
// =============================================================
const addPoint = () => {
  if (isLocked) {
    toast.warning("🔒 Descente verrouillée. Modification impossible.");
    return;
  }

  if (loading) return;

  const trimmedQR = (form.qr ?? "").trim();

  if (!trimmedQR) {
    toast.warning("⚠️ Le code QR est obligatoire !");
    return;
  }

  const normalizedQR = trimmedQR.toUpperCase();

  const qrExists = points.some(
    (p) => p.qr.trim().toUpperCase() === normalizedQR
  );

  if (qrExists) {
    toast.error("🚫 Ce QR existe déjà !");
    return;
  }

  const newPoint: PointForm = {
    ordre: points.length + 1,
    secteur: form.secteur,
    qr: normalizedQR,
    nom: form.nom?.trim() || null,
    adresse: form.adresse?.trim() || null,
    commentaire: form.commentaire?.trim() || null,
    latitude: form.latitude ?? null,
    longitude: form.longitude ?? null,
  };

  const updated = [...points, newPoint];

  setPoints(updated);
  setIsDirty(true);
  setForm(defaultPoint(updated.length + 1));

  toast.success("✅ Point ajouté !");
  inputRef.current?.focus();
};

// =============================================================
// 🗑 Suppression + réindex sécurisé
// =============================================================
const removePoint = (ordre: number) => {
  if (isLocked) {
    toast.warning("🔒 Descente verrouillée. Suppression impossible.");
    return;
  }

  if (!window.confirm("Voulez-vous vraiment supprimer ce point ?")) return;

  const reindexed = points
    .filter((p) => p.ordre !== ordre)
    .map((p, index) => ({
      ...p,
      ordre: index + 1,
    }));

  setPoints(reindexed);
  setIsDirty(true);
  setForm(defaultPoint(reindexed.length + 1));

  toast.info("🗑️ Point supprimé");
};

// =============================================================
// 📤 Synchronisation backend (sécurisée)
// =============================================================
const envoyerVersBackend = async () => {
  if (isLocked) {
    toast.warning("🔒 Descente verrouillée. Synchronisation impossible.");
    return;
  }

  if (!descenteId || isNaN(descenteId)) {
    toast.error("❌ ID de descente invalide.");
    return;
  }

  if (!token) {
    toast.error("🔐 Session expirée. Veuillez vous reconnecter.");
    return;
  }

  if (!isOnline) {
    toast.error("📡 Vous êtes hors ligne.");
    return;
  }

  if (!isDirty) {
    toast.info("ℹ️ Aucun changement à synchroniser.");
    return;
  }

  if (!points?.length) {
    toast.warning("⚠️ Aucun point à synchroniser.");
    return;
  }

  try {
    setLoading(true);

    const payload = {
      points: points.map((p, idx) => ({
        ordre: p.ordre ?? idx + 1,
        secteur: p.secteur,

        // 🔒 normalisation QR
        qr: String(p.qr ?? "").trim().toUpperCase(),

        // 🔥 important pour backend multi-clients
        clientId: p.clientId ?? null,

        nom: p.nom?.trim() || null,
        adresse: p.adresse?.trim() || null,
        commentaire: p.commentaire?.trim() || null,

        latitude:
          typeof p.latitude === "number" ? p.latitude : null,

        longitude:
          typeof p.longitude === "number" ? p.longitude : null,
      })),
    };

    await api.put(`/descentes/${descenteId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("📡 Points synchronisés avec succès !");

    setIsDirty(false);

    localStorage.removeItem(`${LOCAL_KEY}${descenteId}`);

  } catch (err: any) {
    console.error("Erreur sync:", err);

    toast.error(
      "❌ Erreur synchronisation : " +
        (err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Erreur inconnue")
    );

  } finally {
    setLoading(false);
  }
};
 
// =============================================================
// 📤 Export JSON & CSV
// =============================================================
const exportJSON = () => {
  if (!points.length) {
    toast.warning("⚠️ Aucun point à exporter.");
    return;
  }

  const blob = new Blob(
    [JSON.stringify(points, null, 2)],
    { type: "application/json" }
  );

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `points-descente-${descenteId}.json`;
  link.click();

  URL.revokeObjectURL(url);

  toast.info("📦 Export JSON terminé");
};

const exportCSV = () => {
  if (!points.length) {
    toast.warning("⚠️ Aucun point à exporter.");
    return;
  }

  const escape = (value: any) =>
    `"${String(value ?? "")
      .replace(/"/g, '""')
      .replace(/\n/g, " ")
      .replace(/\r/g, " ")}"`;

  const header = "Ordre,Secteur,QR,Nom,Adresse,Commentaire\n";

  const rows = points
    .map(
      (p) =>
        `${p.ordre},${p.secteur},${escape(p.qr)},${escape(p.nom)},${escape(
          p.adresse
        )},${escape(p.commentaire)}`
    )
    .join("\n");

  const blob = new Blob([header + rows], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `points-descente-${descenteId}.csv`;
  link.click();

  URL.revokeObjectURL(url);

  toast.info("📊 Export CSV terminé");
};

  // =============================================================
  // 🎨 Rendu
  // =============================================================
  return (
<div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-gray-100 px-4 sm:px-6 py-8 flex justify-center">
  <ToastContainer position="top-right" />

  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="w-full max-w-5xl bg-slate-900/70 backdrop-blur-md rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] border border-slate-700/60 p-6 sm:p-8 space-y-6"
  >

{/* ================= HEADER ================= */}
<div className="flex justify-between items-center flex-wrap gap-3">
  <h1 className="text-2xl font-bold text-white flex items-center gap-2 flex-wrap">
    📍 Points QR – Descente #{descenteId}

    <span className="text-xs bg-blue-700 px-2 py-1 rounded">
      {points.length} points
    </span>

    {isDirty && (
      <span className="text-xs bg-yellow-600 px-2 py-1 rounded animate-pulse">
        Non synchronisé
      </span>
    )}

    {!isOnline && (
      <span className="text-xs bg-red-600 px-2 py-1 rounded">
        Hors ligne
      </span>
    )}
  </h1>

  <div className="flex gap-2">
{canEdit && (
  <button
    onClick={envoyerVersBackend}
    disabled={loading || !isDirty || !isOnline}
    className={`px-4 py-2 rounded-lg shadow-md transition text-white
      ${
        loading || !isDirty || !isOnline
          ? "bg-teal-800 cursor-not-allowed opacity-50"
          : "bg-teal-600 hover:bg-teal-700"
      }
    `}
  >
    {loading ? "⏳ Synchronisation..." : "📤 Synchroniser"}
  </button>
)}

    <button
      onClick={() => navigate(-1)}
      className="bg-slate-700 hover:bg-slate-600 text-gray-100 px-4 py-2 rounded-lg shadow-md transition"
    >
      ◀ Retour
    </button>
  </div>
</div>

{/* ================= FORM ADD ================= */}
{canEdit && !isLocked && (
  <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-700 space-y-5">
    
    <h2 className="text-lg font-semibold text-gray-200">
      ➕ Ajouter un point
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      {/* Ordre */}
      <input
        type="number"
        value={form.ordre}
        readOnly
        disabled
        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-gray-500 text-center cursor-not-allowed"
      />

      {/* Secteur */}
      <select
        value={form.secteur}
        disabled={loading}
        onChange={(e) =>
          setForm({ ...form, secteur: e.target.value as SecteurPoint })
        }
        className="bg-slate-900 border border-slate-700 text-gray-100 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      >
        {Object.keys(secteurLabel).map((k) => (
          <option key={k} value={k}>
            {secteurLabel[k as SecteurPoint]}
          </option>
        ))}
      </select>

      {/* QR */}
      <input
        ref={inputRef}
        type="text"
        value={form.qr}
        disabled={loading}
        onChange={(e) => setForm({ ...form, qr: e.target.value })}
        placeholder="Scanner ou saisir le code QR"
        className="bg-slate-900 border border-slate-700 text-gray-100 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 md:col-span-2 disabled:opacity-50"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addPoint();
          }
        }}
      />

      {/* Nom */}
      <input
        type="text"
        value={form.nom ?? ""}
        disabled={loading}
        onChange={(e) => setForm({ ...form, nom: e.target.value })}
        placeholder="Nom du point"
        className="bg-slate-900 border border-slate-700 text-gray-100 rounded-lg px-3 py-2 disabled:opacity-50"
      />

      {/* Adresse */}
      <input
        type="text"
        value={form.adresse ?? ""}
        disabled={loading}
        onChange={(e) => setForm({ ...form, adresse: e.target.value })}
        placeholder="Adresse"
        className="bg-slate-900 border border-slate-700 text-gray-100 rounded-lg px-3 py-2 disabled:opacity-50"
      />
    </div>

    {/* Bouton */}
    <div className="flex justify-end">
      <button
        onClick={addPoint}
        disabled={loading || !form.qr?.trim()}
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg shadow-md transition"
      >
        {loading ? "⏳ Traitement..." : "➕ Ajouter le point"}
      </button>
    </div>

  </div>
)}

{/* ================= LISTE ================= */}
<div className="bg-slate-900/40 rounded-lg p-4 border border-slate-700">
  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
    <h2 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
      📋 Liste des points

      {isLocked && (
        <span className="text-xs bg-red-700 px-2 py-1 rounded">
          🔒 Verrouillée
        </span>
      )}

      {!isOnline && (
        <span className="text-xs bg-yellow-700 px-2 py-1 rounded">
          📡 Hors ligne
        </span>
      )}

      {isDirty && (
        <span className="text-xs bg-orange-700 px-2 py-1 rounded animate-pulse">
          Non synchronisé
        </span>
      )}
    </h2>

    {points.length > 0 && (
      <div className="flex gap-2">
        <button
          onClick={exportCSV}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded-lg text-sm transition"
        >
          📊 Export CSV
        </button>

        <button
          onClick={exportJSON}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded-lg text-sm transition"
        >
          📦 Export JSON
        </button>
      </div>
    )}
  </div>

  <div className="overflow-x-auto rounded-lg border border-slate-700 shadow">
  <table className="min-w-full text-sm">
    <thead className="bg-slate-800 text-gray-300 uppercase text-xs border-b border-slate-700">
      <tr>
        <th className="p-3 text-left">Descente</th> {/* Nouvelle colonne */}
        <th className="p-3 text-left">#</th>
        <th className="p-3 text-left">Secteur</th>
        <th className="p-3 text-left">QR</th>
        <th className="p-3 text-left">Nom</th>
        <th className="p-3 text-left">Adresse</th>
        <th className="p-3 text-right">Action</th>
      </tr>
    </thead>

    <tbody>
      {points.length > 0 ? (
        points.map((p, index) => (
          <tr
            key={`${p.qr}-${index}`}
            className="border-t border-slate-700 hover:bg-slate-800 transition"
          >
            {/* Colonne Descente */}
            <td className="p-3 font-semibold text-gray-200">
              {descenteNumero ?? descenteId}
            </td>

            <td className="p-3 font-semibold text-gray-200">
              {p.ordre}
            </td>

            <td className="p-3 text-gray-300">
              {secteurLabel[p.secteur]}
            </td>

            <td className="p-3 font-mono text-gray-300 break-all">
              {p.qr}
            </td>

            <td className="p-3 text-gray-200">
              {p.nom || (
                <span className="text-slate-500 italic">—</span>
              )}
            </td>

            <td className="p-3 text-gray-300">
              {p.adresse || (
                <span className="text-slate-500 italic">—</span>
              )}
            </td>

            <td className="p-3 text-right">
              {canEdit && (
                <button
                  onClick={() => removePoint(p.ordre)}
                  disabled={isLocked || loading}
                  className={`text-sm transition
                    ${
                      isLocked || loading
                        ? "text-slate-500 cursor-not-allowed"
                        : "text-red-400 hover:text-red-300"
                    }
                  `}
                >
                  🗑 Supprimer
                </button>
              )}
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td
            colSpan={7}
            className="text-center text-gray-500 py-6 italic"
          >
            {loading
              ? "⏳ Chargement..."
              : "Aucun point ajouté pour le moment."}
          </td>
        </tr>
      )}
    </tbody>
  </table>

  
</div>
</div>

{/* ================= FOOTER ================= */}
<div className="pt-6 border-t border-slate-700 text-center text-xs text-slate-400 space-y-2">

  <p className="tracking-wide">
    © {new Date().getFullYear()}{" "}
    <span className="text-white font-semibold">
      EspoirAfriqueSas
    </span>
    . Tous droits réservés.
  </p>

  <p className="text-slate-500">
    Plateforme SaaS de gestion opérationnelle
  </p>

  <p className="text-slate-600">
    Version <span className="text-slate-400 font-medium">Platinum PRO</span>
  </p>

  <p className="text-slate-600">
    Développé par{" "}
    <span className="text-slate-300 font-medium">
      Tengs-Labs
    </span>
  </p>

</div>
      </motion.div>
    </div>
  );
};
 
export default PointsParDescente;